import { ArrowLeftOutlined, AppstoreOutlined } from '@ant-design/icons';
import { Button, Modal, Space, notification } from 'antd';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Hand } from '../components/Hand';
import { HoldingMessageScene } from '../components/HoldingMessageScene';
import { PageHead } from '../components/PageHead';
import { api } from '../lib/api';
import { sanitiseEmbed } from '../lib/sanitiseEmbed';
import { showTeamMessage } from '../lib/teamMessage';
import { useAuth } from '../lib/auth';
import { getPlatformString, isWindowVisible } from '../lib/clientInfo';
import { wsUrl } from '../lib/config';
import { useWebSocket } from '../lib/ws';

dayjs.extend(calendar);

type EventInfo = {
  id: string;
  title: string;
  date: string | null;
  status: 'draft' | 'live' | 'ended';
  embed_code: string | null;
  rehearsal_vmix_id: string | null;
};

type IncomingMessage =
  | { type: 'go-live'; vmixId: string }
  | { type: 'go-waiting' }
  | { type: 'event-status'; status: 'draft' | 'live' | 'ended' }
  | { type: 'hand'; speakerId: string; raised: boolean; loweredBy: 'team' | 'speaker' | null }
  | { type: 'refresh'; speakerId: string }
  | {
      type: 'message';
      speakerId?: string;
      text: string;
      level: 'info' | 'warning' | 'urgent';
      sentAt: number;
    };

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;

function formatCountdown(target: Date): string {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return 'any moment';
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];
  if (days) parts.push(`${days}d`);
  if (hours || days) parts.push(`${hours}h`);
  if (minutes || hours || days) parts.push(`${minutes}m`);
  parts.push(`${seconds}s`);
  return parts.join(' ');
}

export function WaitingRoomScene() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { session, setSession } = useAuth();
  const [info, setInfo] = useState<EventInfo | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    const fetchInfo = async () => {
      try {
        const data = await api<EventInfo>(`/api/event/${eventId}/info`);
        if (!cancelled) setInfo(data);
      } catch {
        // ignore; holding screen still renders
      }
    };
    void fetchInfo();
    // Re-fetch when the speaker comes back to the tab so updates the team
    // pushed mid-event (embed code, rehearsal key, status) appear without a
    // full refresh.
    const onFocus = () => void fetchInfo();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [eventId]);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const url = useMemo(() => {
    if (!eventId || !session) return null;
    return wsUrl(
      `/api/event/${eventId}/waiting?role=speaker&token=${encodeURIComponent(session.token)}`,
    );
  }, [eventId, session]);

  const [handRaised, setHandRaised] = useState(false);

  const { send } = useWebSocket<IncomingMessage>(
    url,
    (msg) => {
      if (msg.type === 'go-live' && eventId) {
        notification.success({
          message: 'You are going on air',
          description: 'Moving you to the stage now.',
          placement: 'topRight',
        });
        navigate(`/stage/${eventId}?vmix=${encodeURIComponent(msg.vmixId)}`);
      } else if (msg.type === 'event-status') {
        setInfo((prev) => (prev ? { ...prev, status: msg.status } : prev));
      } else if (msg.type === 'hand') {
        setHandRaised(msg.raised);
        if (!msg.raised && msg.loweredBy === 'team') {
          notification.info({
            message: 'Your hand was lowered by the team.',
            placement: 'topRight',
          });
        }
      } else if (msg.type === 'refresh') {
        window.location.reload();
      } else if (msg.type === 'message') {
        showTeamMessage(msg.text, msg.level);
      }
    },
    {
      onOpen: (sendImmediately) => {
        sendImmediately({
          type: 'client-info',
          platform: getPlatformString(),
          visible: isWindowVisible(),
          location: window.location.pathname,
        });
      },
    },
  );

  useEffect(() => {
    const onVisibility = () => {
      send({ type: 'client-info', visible: isWindowVisible() });
    };
    const onFocus = () => send({ type: 'client-info', visible: true });
    const onBlur = () => send({ type: 'client-info', visible: false });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, [send]);

  const eventDate = info?.date ? new Date(info.date) : null;
  const isEarly =
    eventDate && eventDate.getTime() - Date.now() > TWO_HOURS_MS;
  const isEnded = info?.status === 'ended';

  let title = 'Waiting Room';
  let message: React.ReactNode =
    "Please stay on this page. The team will move you on air shortly.";
  let isLoading = true;

  if (isEnded) {
    title = 'Event Finished';
    message = 'This event has finished and is no longer available to speakers.';
    isLoading = false;
  } else if (isEarly && eventDate && info) {
    title = "You're early";
    const calStr = dayjs(eventDate).calendar(null, {
      sameDay: '[at] LT',
      nextDay: '[tomorrow at] LT',
      nextWeek: '[on] DD/MM/YYYY [at] LT',
      lastWeek: '[on] DD/MM/YYYY [at] LT',
      sameElse: '[on] DD/MM/YYYY [at] LT',
    });
    message = (
      <>
        The <strong>{info.title}</strong> waiting room opens {calStr}.
        <br />
        <span style={{ color: '#888' }}>Starts in {formatCountdown(eventDate)}</span>
      </>
    );
    isLoading = false;
  }

  const handleSignOut = () => {
    Modal.confirm({
      title: 'Sign out?',
      onOk: () => {
        setSession(null);
        navigate('/login', { replace: true });
      },
    });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      <PageHead title={info?.title ?? 'Waiting Room'} />
      <div
        style={{
          padding: '16px 24px',
          display: 'flex',
          gap: 8,
          alignItems: 'center',
        }}
      >
        <Button
          type="text"
          icon={<AppstoreOutlined />}
          onClick={() => navigate('/me')}
        >
          My events
        </Button>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleSignOut}>
          Sign out
        </Button>
      </div>
      {info?.embed_code && !isEnded && (
        <div
          style={{
            maxWidth: 900,
            margin: '24px auto',
            padding: '0 16px',
            textAlign: 'center',
          }}
        >
          <div
            // Wraps the embed in a 16:9 responsive container so iframes that
            // don't carry their own dimensions still render visibly.
            style={{
              position: 'relative',
              paddingTop: '56.25%',
              background: '#000',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
              ref={(el) => {
                if (!el) return;
                if (el.dataset.code !== info.embed_code) {
                  // Sanitise BEFORE injecting — strips everything except
                  // allow-listed iframe hosts to defend against admin
                  // accounts pasting <script> tags into events.embed_code
                  // and stealing speaker sessions.
                  const safe = sanitiseEmbed(info.embed_code ?? '');
                  el.innerHTML = safe;
                  el.dataset.code = info.embed_code ?? '';
                  el.querySelectorAll('iframe').forEach((iframe) => {
                    iframe.style.width = '100%';
                    iframe.style.height = '100%';
                    iframe.style.border = '0';
                  });
                }
              }}
            />
          </div>
        </div>
      )}
      <HoldingMessageScene
        title={title}
        message={message}
        isLoading={isLoading}
        shouldShowLogo={!info?.embed_code}
      >
        {!isEnded && !isEarly && (
          <Space direction="vertical" align="center" size={16}>
            {info?.rehearsal_vmix_id && (
              <Button
                size="large"
                shape="round"
                onClick={() => eventId && navigate(`/rehearse/${eventId}`)}
              >
                🎤 Test mic &amp; camera
              </Button>
            )}
            {handRaised ? (
              <Space>
                <Hand size={36} />
                <Button
                  size="large"
                  shape="round"
                  onClick={async () => {
                    setHandRaised(false);
                    if (eventId) {
                      await api(`/api/event/${eventId}/hand`, {
                        method: 'POST',
                        body: JSON.stringify({ raised: false }),
                      });
                    }
                  }}
                >
                  Lower Hand
                </Button>
              </Space>
            ) : (
              <Button
                size="large"
                shape="round"
                onClick={async () => {
                  setHandRaised(true);
                  if (eventId) {
                    await api(`/api/event/${eventId}/hand`, {
                      method: 'POST',
                      body: JSON.stringify({ raised: true }),
                    });
                  }
                }}
              >
                Raise Hand
              </Button>
            )}
          </Space>
        )}
      </HoldingMessageScene>
      <div style={{ display: 'none' }} aria-hidden>
        {/* re-render every second so the countdown updates */}
        {tick}
      </div>
    </div>
  );
}
